import { useId } from 'react';
import { planVersionContentSchema, type PlanVersionContent } from '@traceio-org/platform-contracts';

/** One problem with the content a staff member typed, at its path (components.0.key). */
export interface ContentIssue {
  path: string;
  message: string;
}

/**
 * Checks pasted or edited content: valid JSON first, then the contract's plan version schema, which also holds
 * the cross-field rules (unique component keys, one metered component, a discount on the metered component, one
 * estimate per action). platform-api checks the same schema again plus the module catalog, and answers its own
 * issues at their paths. Nothing here prices anything.
 */
export function contentIssues(json: string): { content: PlanVersionContent | null; issues: ContentIssue[] } {
  let value: unknown;
  try {
    value = JSON.parse(json);
  } catch (error) {
    const detail = error instanceof Error ? error.message : 'it could not be parsed';
    return { content: null, issues: [{ path: '', message: `The content is not valid JSON: ${detail}` }] };
  }
  const parsed = planVersionContentSchema.safeParse(value);
  if (parsed.success) return { content: parsed.data, issues: [] };
  return {
    content: null,
    issues: parsed.error.issues.map((issue) => ({
      path: issue.path.map(String).join('.'),
      message: issue.message,
    })),
  };
}

/** The content as a staff member edits it: the JSON of the plan version, indented. */
export function contentText(content: PlanVersionContent): string {
  return JSON.stringify(content, null, 2);
}

export interface ContentFieldProps {
  value: string;
  onChange: (value: string) => void;
  error: string | null;
  /** Every issue found, listed under the field at its path; the first one is also the field's error. */
  issues: readonly ContentIssue[];
  label?: string;
}

/**
 * The JSON of a plan version's content, in a monospace area, with the issues found listed at their paths. The
 * area is described by the issue list, so a screen reader hears every issue, not only the first.
 */
export function ContentField({ value, onChange, error, issues, label = 'Content (JSON)' }: ContentFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const issuesId = `${id}-issues`;
  const describedBy = [hintId, issues.length > 0 ? issuesId : null].filter(Boolean).join(' ');
  return (
    <div className="admin-field" data-invalid={error ? 'true' : undefined}>
      <label className="admin-field__label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        className="admin-input admin-input--area admin-input--code"
        value={value}
        rows={18}
        spellCheck={false}
        onChange={(event) => onChange(event.currentTarget.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
      <p className="admin-field__hint" id={hintId}>
        Currency, billing interval, price components (money in micros of the currency: 1 USD is 1,000,000),
        token estimates (in millitokens: 1 token is 1,000), the billable basis, the allowed modules, the trial, the
        self-serve terms and a source label.
      </p>
      {issues.length > 0 ? (
        <ul className="admin-field__error admin-issues" id={issuesId}>
          {issues.map((issue, index) => (
            <li key={`${issue.path}-${index}`}>
              {issue.path ? (
                <>
                  <code>{issue.path}</code>: {issue.message}
                </>
              ) : (
                issue.message
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
